// ─── walmartAgent.js ──────────────────────────────────────────────────────────
// Real agent for Walmart Open API (via Anthropic mock).
// Outputs { item, brand, price, rating, inStock, store } + Walmart extras.
// ─────────────────────────────────────────────────────────────────────────────

// Calls our local proxy server (server.js) which forwards to Anthropic.
// The API key stays on the server — never exposed to the browser.
const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate a Walmart Open API search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 49.97,
      "original_price": 69.00,
      "rating": 4.3,
      "review_count": 3200,
      "seller_type": "Walmart",
      "seller_name": "Walmart.com",
      "is_two_day": true,
      "shipping_free": true,
      "shipping_cost": 0,
      "item_id": "123456789",
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL product names, brands, and realistic Walmart prices for "${query}"
- Mix seller_type: "Walmart" for 2 items, "3P" for 1 item with a real store name
- original_price higher than price for 2 items (rollback pricing), same for 1
- is_two_day: true for Walmart-direct items, false for 3P
- shipping_free: true when price >= 35 or is_two_day
- Vary ratings 3.5–4.8, review counts 50–30000
- Use 9-digit item_ids
- Price at low / mid / premium tiers for "${query}"`;
}

async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });
  if (!response.ok) throw new Error(`Walmart agent API error: ${response.status}`);
  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';
  const first   = rawText.indexOf('{');
  const last    = rawText.lastIndexOf('}');
  if (first === -1) throw new Error('Walmart agent: no JSON found');
  return JSON.parse(rawText.slice(first, last + 1));
}

function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100) : null;

  return {
    item:           raw.title         || 'Unknown Product',
    brand:          raw.brand         || 'Unknown',
    price,
    rating:         parseFloat(raw.rating) || 4.0,
    inStock:        raw.availability  !== 'Out of Stock',
    store:          store.id,

    // Walmart-specific extras
    item_id:        raw.item_id       || null,
    url:            raw.url           || `https://www.walmart.com/search?q=${encodeURIComponent(raw.title||'')}`,
    original_price: originalPrice,
    discount_pct:   discountPct,
    review_count:   parseInt(raw.review_count) || 0,
    seller_type:    raw.seller_type   || 'Walmart',
    seller_name:    raw.seller_name   || 'Walmart.com',
    is_two_day:     raw.is_two_day    === true,
    shipping_free:  raw.shipping_free === true,
    shipping_cost:  parseFloat(raw.shipping_cost) || 0,
    total_cost:     +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:    'walmart',
  };
}

export async function runWalmartAgent(store, query) {
  const prompt = buildPrompt(query);
  const parsed = await callAPI(prompt);
  const items  = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

export default runWalmartAgent;
